// Runtime for every landing. Everything product-specific was baked into the
// page at build time (src/build.ts) and is read back from data attributes on
// <html>, so this file is shared verbatim by all products.

import { initPosthog, isTrackedHost, trackContact, trackPressInquiry, trackStoreClick, trackTrialClick } from './analytics'
import { pressMailto } from './press'

const { storageKey = '', product = '', locale = 'en' } = document.documentElement.dataset

// PostHog: one project for every product; the key is a public build variable
// from the repo-root .env. Off without a key and off on localhost, unless
// VITE_PUBLIC_POSTHOG_DEBUG=1 says to report local work (for checking it).
{
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined
  const host = (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) ?? 'https://eu.i.posthog.com'
  const debug = import.meta.env.VITE_PUBLIC_POSTHOG_DEBUG === '1'
  if (key && product && (debug || isTrackedHost(location.hostname))) {
    initPosthog({ key, host, product, locale, debug })
  }
}

// EN / PT links: remember the choice so the default-locale page redirects to
// the chosen one next time (and stops doing so after switching back). The
// redirect itself is the inline script in the template head, so it runs
// before any paint.
for (const el of document.querySelectorAll<HTMLAnchorElement>('a[data-lang]')) {
  el.addEventListener('click', () => {
    try {
      if (storageKey) localStorage.setItem(storageKey, el.dataset.lang ?? '')
    } catch {
      // Storage blocked: the link still navigates.
    }
  })
}

// WhatsApp links (only rendered visible when product.json has a number).
for (const el of document.querySelectorAll<HTMLAnchorElement>('a[data-contact]')) {
  el.addEventListener('click', () => trackContact())
}

// Trial links leave for the app's pricing page and Stripe Checkout.
for (const el of document.querySelectorAll<HTMLAnchorElement>('a[data-trial]')) {
  el.addEventListener('click', () => trackTrialClick())
}

// Store badges (app-kind landings) leave for the App Store / Google Play. One
// without a store URL yet (data-soon) only scrolls to the closing band.
for (const el of document.querySelectorAll<HTMLAnchorElement>('a[data-store]:not([data-soon])')) {
  el.addEventListener('click', () => trackStoreClick(el.dataset.store ?? ''))
}

// Press form (app-kind landings, /press/): no backend — the message leaves
// through the visitor's own mail client, addressed to the company. Without
// JS the form's own mailto action does the same, minus the sender line.
const pressForm = document.querySelector<HTMLFormElement>('form[data-press]')
if (pressForm) {
  pressForm.addEventListener('submit', (event) => {
    event.preventDefault()
    const data = new FormData(pressForm)
    const field = (name: string) => String(data.get(name) ?? '')
    location.href = pressMailto({
      to: pressForm.action.replace(/^mailto:/, ''),
      brand: pressForm.dataset.brand ?? '',
      email: field('email'),
      subject: field('subject'),
      message: field('body'),
    })
    trackPressInquiry()
  })
}

// ── Sticky mobile bar ───────────────────────────────────────────────────────
// Tucked away while the trial section is on screen: it would cover the very
// button it points to.

const trialSection = document.getElementById('trial')
const mobileCta = document.getElementById('mobile-cta')

if (trialSection && mobileCta) {
  try {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) mobileCta.classList.toggle('is-hidden', entry.isIntersecting)
      },
      { threshold: 0 },
    )
    observer.observe(trialSection)
  } catch {
    // No IntersectionObserver: the bar simply stays put.
  }
}
